import React, { useEffect, useState } from 'react';
import { AppWindow, ArrowLeft } from 'lucide-react';
import BottomNav from '../../components/common/BottomNav';
import SidebarNav from '../../components/common/SidebarNav';
import TrimesterSelector from '../../features/trimesters/components/TrimesterSelector';
import { useAuth } from '../../features/auth/context/AuthContext';

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  onBack?: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, showBack = false, onBack }) => {
  const { user, currentChurch, applications } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.localStorage.getItem('ieadjo:sidebar-collapsed') === 'true';
  });
  const [selectedApplicationId, setSelectedApplicationId] = useState('');

  useEffect(() => {
    window.localStorage.setItem('ieadjo:sidebar-collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (applications.length === 0) {
      setSelectedApplicationId('');
      return;
    }

    const currentApplication = applications.find((application) => application.slug === 'ebd') ?? applications[0];
    setSelectedApplicationId(currentApplication.id);
  }, [applications]);

  const handleApplicationChange = (applicationId: string) => {
    setSelectedApplicationId(applicationId);

    const application = applications.find((item) => item.id === applicationId);
    if (!application || application.slug === 'ebd') return;

    const targetUrl = new URL(application.launchUrl, window.location.origin);
    window.location.assign(targetUrl.toString());
  };

  return (
    <div className="app-shell min-h-screen lg:flex">
      <SidebarNav collapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed((current) => !current)} />

      <div className="app-container flex min-h-screen min-w-0 flex-1 flex-col lg:max-w-none">
        <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between gap-3 border-b border-gray-100 bg-white/80 px-6 py-4 backdrop-blur-md lg:hidden">
          <div className="min-w-0 flex items-center gap-2">
            {showBack && onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm"
                aria-label="Voltar"
              >
                <ArrowLeft size={18} />
              </button>
            ) : null}
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
              <img src="/logo.png" alt="Logo IEADJO" className="h-full w-full rounded-lg object-cover" />
            </div>
            <h1 className="truncate text-base font-bold tracking-tight text-slate-900">IEADJO EBD</h1>
          </div>
          <div className="flex items-center gap-2">
            {applications.length > 0 && (
              <div className="flex h-9 items-center gap-1 rounded-xl border border-slate-200 bg-white px-2 shadow-sm">
                <AppWindow size={14} className="text-primary" />
                <select
                  value={selectedApplicationId}
                  onChange={(event) => handleApplicationChange(event.target.value)}
                  className="w-24 bg-transparent text-xs font-bold text-slate-700 outline-none"
                  aria-label="Trocar aplicativo"
                >
                  {applications.map((application) => (
                    <option key={application.id} value={application.id}>
                      {application.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <TrimesterSelector />
            <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full border-2 border-white bg-gray-100 shadow-sm">
              <img
                src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(user?.name ?? 'IEADJO')}`}
                alt="User profile"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </header>

        <header className="sticky top-0 z-30 hidden border-b border-slate-200/70 bg-white/85 backdrop-blur lg:block">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-3 py-5">
            <div className="flex justify-start">
              {showBack && onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm"
                  aria-label="Voltar"
                >
                  <ArrowLeft size={20} />
                </button>
              ) : null}
            </div>

            <div className="flex justify-center">
              <TrimesterSelector buttonClassName="h-14 rounded-2xl px-4 text-sm" />
            </div>

            <div className="flex justify-end">
              <div className="flex items-center gap-3">
                {applications.length > 0 && (
                  <div className="flex h-14 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 shadow-sm">
                    <AppWindow size={16} className="text-primary" />
                    <select
                      value={selectedApplicationId}
                      onChange={(event) => handleApplicationChange(event.target.value)}
                      className="w-40 cursor-pointer bg-transparent text-sm font-bold text-slate-700 outline-none"
                      aria-label="Trocar aplicativo"
                    >
                      {applications.map((application) => (
                        <option key={application.id} value={application.id}>
                          {application.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-6 pt-24 pb-24 lg:px-8 lg:pb-10 lg:pt-8">
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>

        <BottomNav />
      </div>
    </div>
  );
};

export default MainLayout;
